import { useState, useCallback, useMemo } from 'react';
import {
  getProfileFormValidationErrors,
  type ProfileDynamicFields,
  type ProfileRequiredFormData,
  type ProfileValidationField,
} from '../pages/users/profileFormValidation';

export function useProfileFormValidation(
  formData: ProfileRequiredFormData,
  dynamicFields: ProfileDynamicFields,
  phoneNumberOverride?: string
) {
  const [touched, setTouched] = useState<Partial<Record<ProfileValidationField, boolean>>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const allErrors = useMemo(
    () => getProfileFormValidationErrors(formData, dynamicFields, phoneNumberOverride),
    [formData, dynamicFields, phoneNumberOverride]
  );

  const touch = useCallback((field: ProfileValidationField) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const touchOnChange = useCallback((field: ProfileValidationField) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const showError = useCallback(
    (field: ProfileValidationField): string | undefined => {
      if (!submitAttempted && !touched[field]) {
        return undefined;
      }
      return allErrors[field];
    },
    [allErrors, submitAttempted, touched]
  );

  const validateAll = useCallback(() => {
    setSubmitAttempted(true);
    const errors = getProfileFormValidationErrors(
      formData,
      dynamicFields,
      phoneNumberOverride
    );
    return Object.keys(errors).length === 0;
  }, [formData, dynamicFields, phoneNumberOverride]);

  const reset = useCallback(() => {
    setTouched({});
    setSubmitAttempted(false);
  }, []);

  const getSubmitDisabledReason = useCallback((): string | undefined => {
    const firstError = Object.values(allErrors)[0];
    return firstError;
  }, [allErrors]);

  const isValid = Object.keys(allErrors).length === 0;

  return {
    showError,
    touch,
    touchOnChange,
    validateAll,
    reset,
    getSubmitDisabledReason,
    isValid,
    allErrors,
  };
}
